import React from 'react';
import styled from 'styled-components';

import {unique} from '../../lib/helpers';
import Card from '../../components/Card';
import Icon from '../../components/Icon';
import Button from '../../components/Button';
import Notification from '../../components/Notification';

import {getValidationErrors, flattenValidationErrors} from './shared';

function Checklist({form, notifications, disabled, saving, isNewForm, validationEnabled}) {
  let validations = getValidationErrors(form, {disabled: !validationEnabled});
  let tmpValidations = validations;

  if (!validationEnabled) {
    // If validations are disabled, we still need to know if any section of the form has errors to show the appropriate
    // check mark. When validations are disabled, we will force their calculation.
    tmpValidations = getValidationErrors(form);
  }

  const nameErrors = flattenValidationErrors(validations.name);
  const tmpNameErrors = validations === tmpValidations 
    ? nameErrors 
    : flattenValidationErrors(tmpValidations.name);
  const questionErrors = [...flattenValidationErrors(validations.questions), ...flattenValidationErrors(validations.questionsById)];
  const tmpQuestionErrors = validations === tmpValidations
    ? questionErrors 
    : [...flattenValidationErrors(tmpValidations.questions), ...flattenValidationErrors(tmpValidations.questionsById)];
  const alertErrors = flattenValidationErrors(validations.alertsById);
  const contextAlertErrors = flattenValidationErrors(validations.contextAlertsById);
  const tmpAlertErrors = validations === tmpValidations
    ? alertErrors
    : flattenValidationErrors(tmpValidations.alertsById);
  const permissionErrors = flattenValidationErrors(validations.reviewers);
  const tmpPermissionErrors = validations === tmpValidations
    ? permissionErrors
    : flattenValidationErrors(tmpValidations.reviewers);

  return (
    <Container>
      <Card>
        <Card.Title>Form Checklist</Card.Title>
        <ChecklistItem
          title="1. Form Name"
          anchor="#form-name"
          errors={unique(nameErrors)}
          complete={tmpNameErrors.length === 0}
        />
        <ChecklistItem
          title="2. Questions"
          anchor="#questions"
          errors={unique(questionErrors)}
          complete={tmpQuestionErrors.length === 0}
        />
        <ChecklistItem
          title="3. Permissions"
          anchor="#permissions"
          errors={unique(permissionErrors)}
          complete={tmpPermissionErrors.length === 0}
        />
        <ChecklistItem
          title="4. Notifications"
          anchor="#notifications"
          complete={!isNewForm || notifications.length > 0}
        />
        <ChecklistItem
          title="5. Alerts"
          anchor="#alerts"
          errors={unique(alertErrors)}
          complete={!isNewForm || (form.alerts.length > 0 && tmpAlertErrors.length === 0)}
        />
        <ChecklistItem
          title="6. ContextAlerts"
          anchor="#alerts"
          errors={unique(contextAlertErrors)}
          complete={!isNewForm || (form.contextAlerts.length > 0 && tmpAlertErrors.length === 0)}
        />
        <ChecklistButton>
          <Button
            block
            disabled={disabled || saving}
            type="submit">
            {saving ? 'Saving...' : (isNewForm ? 'Create Form' : 'Save Form')}
          </Button>
        </ChecklistButton>
      </Card>
    </Container>
  );
}

const Container = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  width: 25rem;
  padding: 1.875rem 1.875rem 0 0;

  @media (max-width: 75rem) {
    display: none;
  }
`;

const ChecklistItem = styled(({title, anchor, complete, errors = [], ...props}) => (
  <div {...props}>
    <ChecklistIcon complete={errors.length === 0 && complete} error={errors.length > 0} />
    <ChecklistItemBody>
      <a href={anchor}>{title}</a>
      {errors.length > 0 && (
        <Notification mini noIcon error>
          <ul>
            {errors.map(error => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </Notification>
      )}
    </ChecklistItemBody>
  </div>
))`
  padding: 0.5rem 1.875rem;
  display: flex;
`;

const ChecklistItemBody = styled.div`
  flex: 1;
  padding: 0.5rem 0 0 0;

  > a {
    display: block;
    margin-bottom: 0.5rem;
  }

  ul {
    font-size: 0.875rem;
  }
`;

const ChecklistIcon = styled(({complete, error, ...props}) => (
  <div {...props}>
    <Icon name={error ? 'alert-circle' : 'check'} size="1.5em" />
  </div>
))`
  padding: 0.5rem;
  background: ${props => props.complete ? '#0049ee' : props.error ? '#B4131A' : '#b2b2b2'};
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  color: #fff;
  margin-right: 0.75rem;
`;

const ChecklistButton = styled.div`
  padding: 1.25rem 1.875rem;
`;

export default Checklist;